import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { Button, Grid, InputAdornment, MenuItem, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DropzoneArea } from 'mui-file-dropzone';
import { Controller, FieldError, useFormContext, useWatch } from 'react-hook-form';
import moment, { Moment } from 'moment/moment';
import { v4 as uuid } from 'uuid';
import Modal from 'components/shared/Modal';
import { ModalProps } from 'store/types/ComponentProps';
import QuestionType from 'store/enums/QuestionType';
import SelectOption from 'store/types/SelectOption';
import { AttributeFileAnswer, AuraSublistQuestion, SublistAnswer } from 'store/types/AuraAttribute';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { getISODateString } from 'util/Format';
import { getRequiredValidationRule, getValidationErrorProps } from 'util/Form';

import commonStyles from 'styles/common.module.scss';
import styles from './AddSublistItemModal.module.scss';
import FileInfo from 'store/types/FileInfo';
import { getQuestionId, getSublistQuestionId } from 'util/Preferences';

interface AddSublistItemModalProps extends ModalProps {
  questions: AuraSublistQuestion[];
  fieldName: string;
}

const MAX_FILE_SIZE = 5000000;
const MAX_FILES_COUNT = 5;

const getQuestionInput = (
  { type, options }: AuraSublistQuestion,
  value: any,
  onChange: any,
  error?: FieldError
): React.ReactElement => {
  const selectOptions: SelectOption[] = options?.length
    ? options.map(({ id, option }) => ({ name: option, id: id }))
    : [];

  switch (type.name) {
    case QuestionType.Dropdown:
      return (
        <TextField
          value={value}
          onChange={onChange}
          {...getValidationErrorProps(error)}
          select={true}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected: any) =>
              selectOptions.find(({ id }) => selected === id)?.name || (
                <span className={commonStyles.placeholder}>{'Please select value'}</span>
              ),
          }}
        >
          {selectOptions.map(({ name, id }) => (
            <MenuItem key={id} value={id}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      );
    case QuestionType.DropdownCustomList:
      return (
        <TextField
          value={value}
          onChange={onChange}
          {...getValidationErrorProps(error)}
          select={true}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected: any) =>
              selectOptions.find(({ name }) => selected === name)?.name || (
                <span className={commonStyles.placeholder}>{'Please select value'}</span>
              ),
          }}
        >
          {selectOptions.map(({ name }) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      );
    case QuestionType.Currency:
      return (
        <TextField
          value={value}
          onChange={onChange}
          {...getValidationErrorProps(error)}
          type={'number'}
          InputProps={{
            inputProps: { min: 0 },
            startAdornment: <InputAdornment position={'start'}>$</InputAdornment>,
          }}
          placeholder={'0'}
        />
      );
    case QuestionType.Text:
      return (
        <TextField
          value={value}
          onChange={onChange}
          {...getValidationErrorProps(error)}
          placeholder={'Enter your answer here...'}
          multiline={true}
          rows={5}
        />
      );
    case QuestionType.Date:
      return (
        <DatePicker
          views={['month', 'year', 'day']}
          value={value || null}
          onChange={onChange}
          renderInput={(props) => <TextField {...props} {...getValidationErrorProps(error)} fullWidth={true} />}
        />
      );
    case QuestionType.File:
      return (
        <DropzoneArea
          maxFileSize={MAX_FILE_SIZE}
          filesLimit={MAX_FILES_COUNT}
          fileObjects={value ?? []}
          onChange={(newFileObjects) => {
            onChange(newFileObjects);
          }}
          onDelete={onChange}
          showAlerts={['error']}
          clearOnUnmount={true}
          showPreviews
          showPreviewsInDropzone={false}
          useChipsForPreview
          previewText=""
          classes={{
            root: styles.dropzone,
            textContainer: styles.dropzoneTextContainer,
            text: styles.dropzoneText,
            icon: styles.dropzoneIcon,
          }}
          previewGridClasses={{
            container: styles.previewContainer,
            item: styles.previewImageContainer,
          }}
          dropzoneProps={{ noDrag: false }}
          previewChipProps={{ className: styles.chip }}
          alertSnackbarProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          }}
        />
      );
    default:
      return <></>;
  }
};

const getQuestionFieldName = (questionId: string): string => `sublistQuestion-${questionId}`;

const AddSublistItemModal: React.FunctionComponent<AddSublistItemModalProps> = ({
  onClose,
  open,
  questions,
  fieldName,
}) => {
  const { control, setValue, reset, getValues } = useFormContext();
  const [referenceKey, setReferenceKey] = useState<string>(uuid());
  const controlFieldNameView = useMemo(() => `${fieldName}-view`, [fieldName]);
  const controlFieldNameData = useMemo(() => `${fieldName}-data`, [fieldName]);
  const sublistAnswers: Array<SublistAnswer[]> = useWatch({ control, name: fieldName });
  const sublistAnswersView = useWatch({ control, name: controlFieldNameView });
  const addButtonDisabled: boolean = useMemo(
    () => !sublistAnswersView || !Object.values(sublistAnswersView)?.every((item) => !!item),
    [sublistAnswersView]
  );

  const generateNewReferenceKey = useCallback(() => {
    setReferenceKey(uuid());
  }, []);

  const getControlName = useCallback(
    (questionId: string) => `${controlFieldNameView}.${getQuestionFieldName(questionId)}`,
    [controlFieldNameView]
  );

  const handleAddItem = useCallback(() => {
    const files = getValues()['sublistTempFiles'] || {};
    const sublistFiles: AttributeFileAnswer = files ? files[controlFieldNameView] : {};

    Object.entries(sublistFiles || {}).forEach(([key, fileArray]) => {
      if (!fileArray?.length) return;

      const questionId = getQuestionId(fieldName);
      const sublistQuestionId = getSublistQuestionId(key);

      setValue(`sublistFiles.${referenceKey}`, {
        questionId,
        sublistQuestionId,
        files: fileArray || [],
      });
    });

    if (fieldName.split('.').length > 1) {
      reset({
        ...getValues(),
        preferences: {
          [fieldName.split('.')[1]]: [
            ...sublistAnswers,
            Object.values(
              controlFieldNameData.split('.').reduce((nextObject: any, key: string) => nextObject[key], getValues())
            ),
          ],
          [controlFieldNameView]: '',
          [controlFieldNameData]: '',
        },
      });
    } else {
      reset({
        ...getValues(),
        [fieldName]: [...sublistAnswers, Object.values(getValues()[controlFieldNameData])],
        [controlFieldNameView]: '',
        [controlFieldNameData]: '',
      });
    }
    generateNewReferenceKey();
  }, [
    getValues,
    controlFieldNameView,
    fieldName,
    generateNewReferenceKey,
    referenceKey,
    setValue,
    reset,
    sublistAnswers,
    controlFieldNameData,
  ]);

  const handleAddItemAndClose = useCallback(() => {
    handleAddItem();
    onClose();
  }, [handleAddItem, onClose]);

  const handleClose = useCallback(() => {
    reset({
      ...getValues(),
      [controlFieldNameView]: '',
      [controlFieldNameData]: '',
    });
    onClose();
  }, [controlFieldNameData, controlFieldNameView, getValues, onClose, reset]);

  const handleChange = useCallback(
    (onChange, { type, id, name }: AuraSublistQuestion) =>
      (e: ChangeEvent<HTMLInputElement> | Moment | FileInfo[]) => {
        if (!e) return;

        const value = Array.isArray(e) ? e : moment.isMoment(e) ? getISODateString(e) || '' : e.target.value;

        setValue(`${controlFieldNameData}.${id}`, {
          referenceKey,
          type: type?.name,
          answer: type?.name === QuestionType.File ? 'File' : value,
          sublistQuestionId: id,
          name,
        });
        onChange(value);
      },
    [controlFieldNameData, referenceKey, setValue]
  );

  return (
    <Modal
      onClose={handleClose}
      open={open}
      title={'Add New Item'}
      actions={
        <Grid {...defaultGridContainerProps}>
          <Grid {...defaultGridItemProps} xs={true}>
            <Button color={'secondary'} variant={'outlined'} onClick={handleClose}>
              {'Cancel'}
            </Button>
          </Grid>
          <Grid {...defaultGridItemProps} xs={'auto'}>
            <Button color={'secondary'} variant={'contained'} onClick={handleAddItem} disabled={addButtonDisabled}>
              {'Add & Create New'}
            </Button>
          </Grid>
          <Grid {...defaultGridItemProps} xs={'auto'}>
            <Button
              color={'secondary'}
              variant={'contained'}
              onClick={handleAddItemAndClose}
              disabled={addButtonDisabled}
            >
              {'Add'}
            </Button>
          </Grid>
        </Grid>
      }
    >
      <Grid {...defaultGridContainerProps}>
        {questions.map((question) => (
          <Grid {...defaultGridItemProps} key={question.id}>
            <h5 className={commonStyles.subTitle}>{question.name}</h5>
            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) =>
                getQuestionInput(question, value, handleChange(onChange, question), error)
              }
              name={
                question.type.name === QuestionType.File
                  ? `sublistTempFiles.${getControlName(question.id)}`
                  : getControlName(question.id)
              }
              control={control}
              defaultValue={question.type.name === QuestionType.File ? [] : ''}
              rules={{ required: getRequiredValidationRule(question.name, false, open) }}
            />
          </Grid>
        ))}
      </Grid>
    </Modal>
  );
};
export default AddSublistItemModal;
